import styled from "styled-components";
import { device } from "../device";

export enum TileSizes {
  xs, s, m, l
}

export interface TileProps {
  size: TileSizes;
}

export interface GridProps {
  twoColumns: boolean;
}

export const BlogContentGrid = styled.ul<GridProps>`
    position: relative;
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    column-gap: 24px;
    row-gap: 24px;
    width: 100%;
    flex-flow: row wrap;
    align-items: stretch;
    justify-content: flex-start;
    margin-left: 0;
    padding-inline-start: 0;
    margin-top: 0;
    margin-bottom: 24px;

     @media ${device.tablet} {
      column-gap: 32px;
      row-gap: 32px;
      margin-bottom: 32px;
      ${({ twoColumns }) => twoColumns ? `
        grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
      ` : `
        grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr);
      `}
    }
`;

export const HeroPostContainer = styled.li`
    list-style: none;
    grid-column: span 1;
    @media ${device.tablet} {
      grid-column: span 2;
    }
    cursor: pointer;

    a, .inner-link-wrapper {
      flex-direction: column;

      @media ${device.tablet} {
        flex-direction: row;
      }
      width: 100%;
      height: 100%;
      text-decoration: none;

      background-color: #fff;
      display: flex;
      border-radius: 16px;
      overflow: hidden;
      z-index: 0;
      position: relative;

      &:hover {
          .tile-media::after {
              background: rgba(0,0,0,0.15);
          }

          img {
              transform: scale(1.03);
          }
      } 
    }
`;

export const PostContainer = styled.li<TileProps>`
    list-style: none;
    grid-column: span 1;
    cursor: pointer;

    a, .inner-link-wrapper {
      height: 100%;
      text-decoration: none;
      display: flex;
      overflow: hidden;
      z-index: 0;
      position: relative;

      ${({ size }) => size === TileSizes.m && `
          flex-direction: column;
          border-radius: 16px;
          background-color: #fff;
      `}

      ${({ size }) => size === TileSizes.s && `
          grid-column: span 1 / 3;
          flex-direction: column;
          border-radius: 16px;
          background-color: #fff;
      `}
      
      &:hover {
        .tile-media::after {
            background: rgba(0,0,0,0.15);
        }

        img {
            transform: scale(1.03);
        }
      } 
    }
`;

export const TileMedia = styled.div<TileProps>`
  direction: ltr;
  position: relative;
  z-index: 0;
  flex-grow: 0;
  flex-shrink: 0;
  overflow: hidden;
  width: 100%;
  height: 192px;
  min-height: auto;
  flex-basis: auto;
  flex-shrink: 1;

  @media ${device.tablet} {
    ${({ size }) => size === TileSizes.s && `
      width: 100%;
      height: 192px;
      min-height: auto;
      flex-basis: auto;
      flex-shrink: 1;
    `}
    ${({ size }) => size === TileSizes.m && `
      width: 100%;
      height: 294px;
      min-height: auto;
      flex-basis: auto;
      flex-shrink: 1;
    `}

    ${({ size }) => size === TileSizes.l && `
      width: 100%;
      height: auto;
      min-height: 362px;
      flex: 2;
      flex-shrink: 1;
    `}
  }

  &::after {
    content: '';
    display: inline-block;
    width: 100%;
    height: 100%;
    background: rgba(0,0,0,0);
    position: absolute;
    top: 0;
    left: 0;
    z-index: 2;
    -webkit-transition: background 400ms cubic-bezier(0.4, 0, 0.25, 1) 0ms;
    transition: background 400ms cubic-bezier(0.4, 0, 0.25, 1) 0ms;
  }

  img {
      transition: opacity 1s cubic-bezier(0.4, 0, 0.25, 1) 0ms,transform 400ms cubic-bezier(0.4, 0, 0.25, 1) 0ms,-webkit-transform 400ms cubic-bezier(0.4, 0, 0.25, 1) 0ms;
      object-fit: cover;
  }
`;

export const TileDescription = styled.div<TileProps>`
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    flex-grow: 1;
    padding: 24px;
    justify-content: space-between;
    flex-basis: auto;

    @media ${device.tablet} {
      ${({ size }) => size === TileSizes.l && `
        padding: 32px;
        justify-content: space-between;
        flex-basis: 0;
      `}
      
      ${({ size }) => size === TileSizes.m && `
        padding: 32px;
        justify-content: space-between;
        flex-basis: auto;
      `}

      ${({ size }) => size === TileSizes.s && `
        padding: 24px;
        justify-content: space-between;
        flex-basis: auto;
      `}
    }
`;

export const TileDescriptionCategory = styled.div<TileProps>`
  font-size: 12px;
  line-height: 1.33337;
  font-weight: 800;
  color: #6e6e73;
  margin-bottom: 4px;
 
  @media ${device.tablet} {
    ${({ size }) => (size === TileSizes.l || size === TileSizes.m) && `
      margin-bottom: 8px;
    `}
    
    ${({ size }) => size === TileSizes.s && `
      margin-bottom: 4px;
    `}
  }
`;

export const TileDescriptionHeadline = styled.div<TileProps>`
  font-weight: 800;
  color: #1d1d1f;
  display: -webkit-box;
  overflow: hidden;
  font-size: 19px;
  line-height: 25px;
  
  @media ${device.tablet} {
    ${({ size }) => size === TileSizes.l && `
      font-size: 36px;
      line-height: 46px;
    `}
    
    ${({ size }) => size === TileSizes.m && `
      font-size: 27px;
      line-height: 34px;
    `}
    ${({ size }) => size === TileSizes.s && `
      font-size: 19px;
      line-height: 25px;
    `}
  }
`;

export const TileTimestamp = styled.div`
    font-size: 14px;
    line-height: 1.28577;
    font-weight: 700;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    color: #6e6e73;
    margin-top: 8px;
`;

export const SectionSeperator = styled.hr`
    background-color: #d0d0d0;
    height: 1px;
`;

export const BlogContentSection = styled.section`
    h2 {
        font-size: 32px;
        line-height: 1.125;
        font-weight: 800;
        margin-top: 42px;
        margin-bottom: 24px;
    }
`
export const MoreBlogPostsSection = styled.section`
    padding-top: 80px;
    padding-bottom: 64px;
    overflow: hidden;
    
    h2 {
        font-size: 32px;
        line-height: 1.125;
        font-weight: 800;
        margin-top: 0px;
        margin-bottom: 24px;
    }
`

export const ArchiveLink = styled.div`
    font-size: 17px;
    line-height: 1.23536;
    font-weight: 500;
    letter-spacing: -.022em;
    text-align: center;
    margin-top: 32px;
    
    a {
      display: block;
      align-self: center;
      margin: auto;
      width: fit-content;
    }
`